/// <reference types="vite-plugin-pwa/client" />
import { initSentry } from './services/sentryService';

// Initialize Sentry before anything else — captures early errors
initSentry();

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { registerSW } from 'virtual:pwa-register';
import { AuthProvider } from './contexts/AuthContext';
import { AppDataProvider } from './contexts/AppDataContext';
import { UploadManagerProvider } from './contexts/UploadManagerContext';

import { GlobalErrorBoundary } from './components/common/GlobalErrorBoundary';
import { BootShield } from './components/BootShield';
import { UpdateToast, announceUpdateReady } from './components/UpdateToast';

// Dekko Design System (tokens, typography, utilities, animations)
import './styles/index.css';

// Register service worker for PWA — skip in dev to avoid stale cache issues
// Using 'prompt' mode: new SW waits in background, activates on next navigation.
// No auto-reload — prevents the double-load flash after each deploy.
if (typeof window !== 'undefined' && import.meta.env.PROD) {
  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Defer update check until after the app is interactive —
        // checking immediately can activate a new SW that kills
        // in-flight Firestore connections, causing the loading screen to hang.
        setTimeout(() => registration.update(), 5000);
      }
    },
    // A new build is waiting. skipWaiting stays false at BUILD time (see
    // vite.config.ts) so the old page keeps its intact cache; the user opts
    // in here, and updateSW(true) posts SKIP_WAITING then reloads, so the
    // old page never outlives the cache it was serving from.
    onNeedRefresh() {
      announceUpdateReady(() => updateSW(true));
    },
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <AppDataProvider>
          <UploadManagerProvider>
            <GlobalErrorBoundary>
              <App />
            </GlobalErrorBoundary>
            {/* Boot shield overlays the app while it boots, then lifts once. */}
            <BootShield />
            <UpdateToast />
          </UploadManagerProvider>
        </AppDataProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// The pre-bundle static splash (#boot-splash-static) is ADOPTED by BootShield,
// which fades and removes it when boot completes — removing it here restarted
// every enter animation when the React twin mounted (the splash stutter).